/**
 * @file useAsyncQuery.ts
 * @fileoverview Implementeert een generieke hook voor asynchrone data-aanroepen.
 * Het beheert de laad- en foutstatus en biedt een herlaadfunctie
 * voor componenten die afhankelijk zijn van externe gegevens.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const recentlyLoggedErrors = new Map<string, number>();
const ERROR_LOG_DEDUPE_WINDOW_MS = 2500;

interface QueryError {
  message?: string;
  details?: unknown;
  code?: string;
  hint?: string;
}

/**
 * Interface voor het resultaat van een asynchrone query.
 * @template T - Het type van de data die wordt teruggegeven.
 */
export interface AsyncQueryResult<T> {
  /** De opgehaalde data, of `null` als er geen data is. */
  data: T | null;
  /** Geeft aan of de query momenteel wordt uitgevoerd. */
  loading: boolean;
  /** Een eventuele foutmelding als string, of `null` als er geen fout is. */
  error: string | null;
  /** Een functie om de query opnieuw uit te voeren. */
  reload: () => Promise<void>;
}

/**
 * Een generieke custom hook voor het uitvoeren van asynchrone queries.
 * Het abstraheert de state management voor data, laadstatus en fouten.
 *
 * @template T - Het type van de data die de query zal teruggeven.
 * @param {() => Promise<{ data: T | null; error: any }>} queryFn - De asynchrone functie die de query uitvoert.
 * @param {any[]} [deps=[]] - Een array van dependencies voor de `useCallback` hook die de `queryFn` omhult.
 * @param {T | null} [initialData=null] - De initiële data die moet worden gebruikt voordat de query is voltooid.
 * @param {string} [debugLabel] - Optioneel label voor foutlogging.
 * @param {boolean} [preserveDataOnReload=false] - Behoud bestaande data tijdens een handmatige reload.
 * @returns {AsyncQueryResult<T>} Een object met de data, laadstatus, fout en een herlaadfunctie.
 */
export function useAsyncQuery<T>(
  queryFn: () => Promise<{ data: T | null; error: unknown }>,
  deps: readonly unknown[] = [],
  initialData: T | null = null,
  debugLabel?: string,
  preserveDataOnReload = false,
): AsyncQueryResult<T> {
  const [data, setData] = useState<T | null>(initialData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initialDataRef = useRef<T | null>(initialData);

  useEffect(() => {
    initialDataRef.current = initialData;
  }, [initialData]);

  // Capture caller location during render; async stacks are too noisy/minified.
  const inferredLabel = useMemo(() => {
    const stack = new Error().stack?.split('\n') || [];
    const interesting = stack.find((line) => {
      if (!line.includes('.tsx') && !line.includes('.ts')) return false;
      if (line.includes('useAsyncQuery.ts')) return false;
      if (line.includes('chunk-')) return false;
      if (line.includes('@react-refresh')) return false;
      if (line.includes('/node_modules/')) return false;
      return true;
    });
    return interesting?.trim() || 'unknown-caller';
  }, []);

  const memoizedQuery = useCallback(queryFn, deps);

  const execute = useCallback(async (preserveCurrentData = false) => {
    setLoading(true);
    setError(null);
    if (!preserveCurrentData) setData(initialDataRef.current);
    try {
      const { data, error } = await memoizedQuery();
      if (error) throw error;
      setData(data ?? null);
    } catch (err: unknown) {
      const queryError = err as QueryError;
      const message = queryError?.message ?? 'Error while fetching data';
      const label = debugLabel || inferredLabel;
      const key = `${label}::${message}`;
      const now = Date.now();
      const lastLoggedAt = recentlyLoggedErrors.get(key) ?? 0;
      if (now - lastLoggedAt > ERROR_LOG_DEDUPE_WINDOW_MS) {
        recentlyLoggedErrors.set(key, now);
        console.error('[useAsyncQuery]', {
          label,
          message,
          details: queryError?.details,
          code: queryError?.code,
          hint: queryError?.hint,
        });
      }
      setError(queryError?.message ?? 'Error while fetching data');
    } finally {
      setLoading(false);
    }
  }, [memoizedQuery, debugLabel, inferredLabel]);

  useEffect(() => {
    execute(false);
  }, [execute]);

  const reload = useCallback(
    () => execute(preserveDataOnReload),
    [execute, preserveDataOnReload],
  );

  return { data, loading, error, reload };
}
